import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Box, createStyles, Drawer, makeStyles, Theme, Typography } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { useDispatch, useSelector } from 'react-redux';
import BrandSVG from '€components/svgs/loan-logo.svg';
import PrimaryButton from '€components/PrimaryButton';
import { setVisibility } from '€slices/pwa';
import { getVisibilityState } from '€slices/pwa/pwa-selectors';
import PwaInstallContext from './PwaInstallContext';
import { isInStandaloneMode } from '€lib/browser/browser';
import { Hidden } from '€components/Hidden';

interface PwaInstallPopupChromiumProps {
    forceDisplay?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            '& .MuiPaper-root': {
                [theme.breakpoints.up('sm')]: {
                    marginBottom: '3.125rem',
                    maxWidth: '69rem',
                    margin: 'auto',
                },
            },
        },
        closeIcon: {
            '&:hover': {
                cursor: 'pointer',
            },
        },
    }),
);

const PwaInstallPopupChromium: React.FC<PwaInstallPopupChromiumProps> = ({
    forceDisplay = false,
}: PwaInstallPopupChromiumProps) => {
    const dispatch = useDispatch();
    const [isOpen, setOpen] = useState<boolean>(false);
    const isVisible = useSelector(getVisibilityState);
    const { deferredPrompt } = useContext(PwaInstallContext);
    const classes = useStyles();

    const promptToInstall = useCallback(() => {
        if (!deferredPrompt) {
            return null;
        }
        deferredPrompt.userChoice
            .then(({ outcome }) => {
                if (outcome === 'accepted') {
                    setOpen(false);
                }
                return true;
            })
            .catch(error => {
                console.log('deferredPrompt error: ', error);
            });
        return deferredPrompt.prompt();
    }, [deferredPrompt]);

    const handleClose = useCallback(() => {
        dispatch(setVisibility({ isVisible: false }));
        setOpen(false);
    }, [dispatch]);

    useEffect(() => {
        if (forceDisplay || (deferredPrompt && isVisible && !isInStandaloneMode())) {
            setOpen(true);
        }
        return () => {
            dispatch(setVisibility({ isVisible: false }));
        };
    }, [deferredPrompt, isVisible, forceDisplay, dispatch]);

    const header = (
        <Box display="flex">
            <Box display="flex" alignItems="center" justifyContent="center" mx={0.5} mr={2}>
                <Box p={1.13} bgcolor="#2E3D63" borderRadius="50%" display="flex">
                    <BrandSVG transform="scale(0.833)" />
                </Box>
            </Box>
            <Box display="flex" flexDirection="column">
                <Typography variant="h6" color="textPrimary">
                    Loan.co.uk
                </Typography>
                <Typography variant="subtitle2" color="secondary">
                    app.loan.co.uk
                </Typography>
            </Box>
        </Box>
    );

    const promptButton = (
        <PrimaryButton onClick={promptToInstall} style={{ width: '12.94rem' }}>
            Add to Homescreen
        </PrimaryButton>
    );

    return (
        <Drawer
            anchor="bottom"
            open={isOpen}
            onClose={handleClose}
            className={classes.root}
            data-testid="pwa-chromium"
        >
            <Hidden smUp>
                <Box display="flex" flexDirection="column" mt={2} ml={2.5} mr={2}>
                    <Box display="flex" justifyContent="space-between" mb={3.5}>
                        {header}
                        <Box>
                            <CloseIcon onClick={handleClose} data-testid="pwa-close-icon" />
                        </Box>
                    </Box>
                    <Box display="flex" justifyContent="flex-end" mb={2}>
                        {promptButton}
                    </Box>
                </Box>
            </Hidden>
            <Hidden xsDown>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    width="100%"
                    alignItems="center"
                    py={2.5}
                    px={3}
                >
                    <Box display="flex">
                        <Box display="flex">{header}</Box>
                    </Box>
                    <Box display="flex" alignItems="center">
                        <Box mr={4}>{promptButton}</Box>
                        <CloseIcon
                            onClick={handleClose}
                            className={classes.closeIcon}
                            data-testid="pwa-close-icon"
                        />
                    </Box>
                </Box>
            </Hidden>
        </Drawer>
    );
};

export default PwaInstallPopupChromium;
